<template>
<div class="chapter">
<div class="inner_page">
<p><a id="c01"></a></p>
<div class="ct sgc-1">
    NO REASON
  </div>
<div class="tx1">
<b>T</b>HE MORNING RIPENED SLOWLY. TEN O’CLOCK FELT LIKE NOON. July Montgomery cut open a sack of ground feed and poured it into the cement trough. He looked out of the barn window into his hay field, where a low-lying fog stole silently out of the ground, filling space with milky distance. Beyond the fence, the tops of maple, oak, and hickory formed a lumpy, embroidered edge against infinity.
  </div>
<div class="tx">
    July had lived here for more than twenty years, but because of the dreamy quality of the morning, the landscape now appeared almost unfamiliar. The row of round bales of hay—which he’d placed near the road only weeks before—seemed foreign and completely removed from any history that included him. The road itself looked different, and when a hawk stepped off a utility pole, opened its wings, and sailed up the blacktop road toward the nearby village of Words, it disappeared into the looming fog as though entering another world. July marveled at how easily the characters of even the massive, stationary things of reality could be changed by a little moisture in the atmosphere.
  </div>
<div class="tx">
    On the other side of the barn he could hear his small dairy herd hurrying back from the pasture. He had let them out just an hour before, and it seemed odd that they would be coming back. Normally, they preferred to graze all day, knee-deep in grass, even in the most inclement weather.
  </div>
<div class="tx">
    Several cows anxiously butted their heads against the wooden sides of the building and he opened the doors, allowing them back into the barn. Agitated, they bellowed and crowded against each other, milling nervously from one area to the next, swarming in slow motion.
  </div>
<div class="tx">
    Something had frightened them, and July stood in the opening and searched for an explanation—a pack of dogs, perhaps. But he 
     could see nothing, and indeed it wasn’t always possible to identify the reason for a herd’s agitation. Like the fear that often seizes human society, it sometimes had no tangible cause. Given the social nature of animals, an errant yet terrifying idea could flare up in a single limbic system and spread into the surrounding neighborhood, communicated with the speed of a startled flock of birds. Before long, a climate of fear was established, perpetuated through the psyche’s network of instinctual rumor.
  </div>
<div class="tx">
    A movement caught his eye. Several hundred yards away, at the very edge of where the fog swallowed objects wholesale, a large black animal jumped the fence into his hay field, turned around in an almost ritual manner, and looked directly at him.
  </div>
<div class="tx">
    Now there’s something, July thought, staring back. It appeared to be a very big cat, a panther, also known as a cougar, puma, or mountain lion. He’d seen them out west and up north, but never here. Though they had once been native to the area, there had been no reports of them, as far as he knew, for generations. It wasn’t even necessary to actually see one, of course; a stray scent of the beast—inhaled by a single cow—and the whole herd would vibrate with primordial anxiety.
  </div>
<div class="tx">
    Moving slowly, the panther paced with elastic ease along the old fence, carefully measuring its distance from the barn, keeping partially hidden in the fog, like a ghost not willing to assume corporeal form. As it moved, it continued to stare at July, and July continued to look back.
  </div>
<div class="tx">
    He wondered why a panther would reenter an area its ancestors had long ago abandoned. The larger reasons, of course, included the encroachment of human civilization and depletion of natural habitat; but July wondered what the urge itself must have felt like—from the inside—to compel it to leave its familiar haunts. If it was a male, the pursuit of a female might lure it into the unknown; a female, on the other hand, might venture out in search of food or the protective seclusion needed to raise its young. July also imagined that both male and female might, like some people, simply enter an unknown area for the sake of discovering how it compared with what they already knew.
  </div>
<div class="tx">
    As he watched the panther striding slowly, elegantly on the edge of the woods, July also saw no reason to deny to the creature the possibility of acting without a compelling motivation. Perhaps it ended up in his hay field without knowing why it had come.
  </div>
<div class="tx">
    July remembered his own journey to the Driftless Region, more than twenty years ago.
  </div>
<div class="tx">
    He recalled first that nothing had hurt. He’d woken up in a surprisingly comfortable ditch along an unrecognizable road in the middle of the night, near the end of September, somewhere in Wyoming. The stars seemed especially thick and chaotic above him, brilliant but mixed up, as though they had been stirred with a silver oar. He had no memory of how he’d come to be here—wherever <i>here</i> was—and he felt to see if some parts of his body were perhaps broken, bleeding, or missing. But nothing seemed out of place, and nothing hurt.
  </div>
<div class="tx">
    After more checking, he discovered that his wallet was missing. And his duffel bag, lying next to him in the long grass and weeds, had been ransacked. Most of his personal belongings—rope, stove, cooking utensils, hatchet, knife, compass, lantern, bourbon, dried food, candy bars, matches, soap, maps, and a couple books—were gone. All that remained were a couple items of clothing, his sleeping bag, and his water bottle.
  </div>
<div class="tx">
    But nothing hurt and that seemed like a good omen. Things could be much worse. Whoever had left him here had not found the flat canvas money belt tied snuggly around his abdomen. He then fell back to sleep and woke up an hour later at the sound of an approaching vehicle.
  </div>
<div class="tx">
    A pickup moved east along the highway. It was closely followed by a noisy single-axle trailer, pulled by a bumper hitch. As though extending a carpet of light before its path—a carpet it never actually rode on—the truck came to a rattling stop at the nearby intersection. The driver climbed out and walked back to check on the trailer. Cramped from sitting and arthritic with age, he moved stiffly.
  </div>
<div class="tx">
    July dusted off his clothes, walked out of the ditch, and joined the old man at the trailer.
  </div>
<div class="tx">
    “Everything all right?” he asked.
  </div>
<div class="tx">
    The old man seemed startled at not being alone and warily inspected July and the duffel bag extending from his left arm.
  </div>
<div class="tx">
    “So far, so good,” he said, and resumed shining his flashlight through the open slats in the side of the trailer. The dense circle of yellow light moved over a massive Angus bull. The animal’s warm smell had a sweet yet acrid quality and when it shifted its weight from one set of legs to another, the trailer groaned respectfully.
  </div>
<div class="tx">
    July walked to the other side of the road and urinated on the gravel shoulder.
  </div>
<div class="tx">
    It was a clear, summerlike night, and the sky glowed with unusual green luminance.
  </div>
<div class="tx">
    The spilling sound reminded the old man of his own full bladder and he also peed on the edge of the road. Far in the distance a dog barked.
  </div>
<div class="tx">
    “You need a ride, young man?”
  </div>
<div class="tx">
    Inside the truck, the driver adjusted his billed hat and lit a cigarette. July shoved the duffel bag under the seat and sat beside him. “Where you going?” he asked.
  </div>
<div class="tx">
    “Wisconsin. Ever been there?”
  </div>
<div class="tx">
    “Nope,” said July.
  </div>
<div class="tx">
    As they rode through Wyoming, the old man explained that he and his brother kept a herd of Herefords in southwestern Wisconsin. They wanted to breed up some black baldy calves, and the old man had driven out to the stockyards in Cheyenne, looking for a long yearling with eye appeal. At a late auction, he’d bought one.
  </div>
<div class="tx">
    July liked the way the old man talked—his accent and choice of phrases. On this basis he decided to continue with him.
  </div>
<div class="tx">
    “How long you been in Wyoming?” the old man asked.
  </div>
<div class="tx">
    “Eight or nine months, working on a ranch.”
  </div>
<div class="tx">
    “You from around here?”
  </div>
<div class="tx">
    “Nope.”
  </div>
<div class="tx">
    “Where you from?”
  </div>
<div class="tx">
    “Everywhere,” said July. “Never been to Wisconsin, though.”
  </div>
<div class="tx">
    “Where were you before you were in Wyoming?” asked the old man, openly exhibiting the interest of someone who currently lived in the same house he had grown up in.
  </div>
<div class="tx">
    “Unloading ships on the docks in California.”
  </div>
<div class="tx">
    “And before that?”
  </div>
<div class="tx">
    “Hauling wheat in Canada,” July said. His window was open and the warm night air blew against the side of his face. “I spent almost a year in the prairie provinces, driving truck. While I was there I met a man, a logger with a plastic leg who could run faster than anyone I’d ever seen. And at night he’d take off his leg and count the money hidden inside it. Other people were always betting him he couldn’t outrun them.”
  </div>
<div class="tx">
    “How’d he lose his leg?”
  </div>
<div class="tx">
    “Cut it off by mistake with a chain saw, above the knee.”
  </div>
<div class="tx">
    It was the kind of talk people make in bus stations and other places when they do not expect to see the person they’re talking to again—stories about other people, maybe true and maybe not. It was good-natured talk, well suited to the thin, fleeting comfort shared by strangers. Ghost talk.
  </div>
<div class="tx">
    They traded driving in South Dakota and continued all the way into Wisconsin, where the old man began to anticipate returning to his brother and their farm more eagerly.
  </div>
<div class="tx">
    “It’s not that far now,” he said. “Only about twenty miles past the next town. My brother should be waiting up for us. The coffeepot will be on and we can have a real meal.”
  </div>
<div class="tx">
    The trailer rattled loudly after running over a large pothole in the pavement, and the old man stopped at the deserted intersection and went back to check on his young bull. It was dark, and after looking at the tires, he inspected the interior of the trailer with his flashlight.
  </div>
<div class="tx">
    July got out and stretched.
  </div>
<div class="tx">
    When the old man climbed back behind the wheel, July stood in the road and drew the large canvas duffel bag from under the seat. He pulled the strap over his shoulder.
  </div>
<div class="tx">
    “Thanks again for the ride.”
  </div>
<div class="tx">
    “My place is just a little ways ahead. Look, my offer for a place to sleep is good.”
  </div>
<div class="tx">
    “Thanks, but, well, no thanks.”
  </div>
<div class="tx">
    “At least let me drive you into Grange. I don’t feel right leaving you here in the middle of the night.”
  </div>
<div class="tx">
    The young man looked away. He was uncomfortable with not 
     complying with the older man’s wishes yet remained determined to be on his own. “Where does that road go?” he asked, nodding north.
  </div>
<div class="tx">
    “To Words—nothing up there but a handful of houses. Look, my brother will be waiting for me. Our place is only a little ways from here. You can spend the night, and in the morning—”
  </div>
<div class="tx">
    “I wonder why they put so many stop signs here?” asked the young man, neither expecting nor waiting for an answer. “I really appreciate the ride.”
  </div>
<div class="tx">
    Smiling, he closed the door.
  </div>
<div class="tx">
    “Wait,” said the old man. “The sandwiches—there are a couple left. You paid for them.” And he handed a greasy, lumpy paper sack through the open window.
  </div>
<div class="tx">
    July tucked it under his arm. “Well, thanks again, and goodnight.”
  </div>
<div class="tx">
    He stood in the middle of the road and watched the glowing taillights move beyond his sight. The clanking and banging sounds of the trailer faded and disappeared. A grinning yellow moon dissolved all the stars around it and threw a greenish-blue glow over the countryside.
  </div>
<div class="tx">
    July set his pack down and took out a denim jacket, replacing it with the paper sack.
  </div>
<div class="tx">
    “Okay,” he said, “which way now?” He hadn’t thought further ahead than this unknown intersection.
  </div>
<div class="tx">
    He stood in the middle of the road wondering which way to go, waiting for some inspiration—a beckoning or sign. After receiving none, he decided a town called Words was good enough.
  </div>
<div class="tx">
    His boots made clumping sounds against the road’s hard surface, which continued north in a meandering manner up and down hills. Moonlit fields of standing corn, hay, and soybeans merged with evergreen and hardwood, marshland and streams. Crickets, frogs, owls, and other nocturnal creatures called out to him as he passed. Of particular notice were the unidentifiable cries—the raw sounds of nature that refused to be firmly associated with mammal, fowl, or insect.
  </div>
<div class="tx">
    Set off from the road, an occasional yard light burned near a barn. The houses themselves remained dark, their occupants sleeping.
  </div>
<div class="tx">
    It had been some time since he’d been in the Midwest, and July attempted to picture himself in the central part of the United States once again. He’d been born just southwest of Wisconsin, in Iowa, so this seemed like a homecoming of sorts, or as much of one as his habitual homelessness could imagine.
  </div>
<div class="tx">
    In the distance a firefly of light appeared, disappeared, and reappeared at a different location. Once it was out of the hills, it advanced more earnestly, then disappeared for a longer time, only to float up into view a mile away. The single light rounded a corner and divided into two parts, accompanied by a harsh, rushing sound. Then the headlights grew brighter, bigger, and louder, like an instinct merging into consciousness.
  </div>
<div class="tx">
    July stepped off the road, behind a stand of honeysuck le. He’d become accustomed to his own company again and did not wish to share it with anyone or explain where he was going when he didn’t know himself.
  </div>
<div class="tx">
    After he had been walking for another half-hour, the faint yellow glow of a town in the near distance cautioned him to wait for morning before going further. He began looking for a place to pass the night.
  </div>
<div class="tx">
    Beyond the Words Cemetery a collection of old-growth trees ran downhill away from the road. He walked between several dozen gravestones, climbed the woven wire fence, picked his way through mulberry and hazelnut bushes, and found a small hollow of land covered with long grass, sheltered by an overhanging maple. In places, the moonlight fell through the branches and spotted the ground. The thick underbrush he hoped would announce the movement of any large intruders, and the rising slope of the cemetery blocked the view from the road. A short distance further down the hill, the rhythmic burbles of a stream could be heard.
  </div>
<div class="tx">
    July unrolled his sleeping bag. He folded his denim jacket for use as a pillow and ate one of the sandwiches from the paper sack. Then he drank from the water bottle, took off his boots, put his socks inside them, lay down, and zipped himself inside. He loosened the money belt that contained his savings from the past five or six years. Somewhere in the distance a barred owl loosed its mocking cry, 
     “Who-cooks-for-you, who-cooks-for-you-aaaaallllll.” The light from an occasional star found its way through the tree above him, blinking on and off with the shuttered movement of leaves in the wind.
  </div>
<div class="tx">
    Closing his eyes, he tried to place the experiences of the past several days in a reasonable perspective: the drive from Wyoming, the wandering conversation with the old man, the walk down the mostly deserted road. The dark foliage above him seemed to draw nearer and a spirit of fatigue invaded his senses, disrupting his review of recent events. Blocking it out, he focused his attention and struggled for several long minutes to keep the images in his mind from sliding through the cellar door of nonsensical stories, and fell asleep.
  </div>
<div class="tx">
    Hours later, he woke up with sudden, blunt finality. He knew why four stop signs had been placed on a remote intersection: there had been an accident. Some time ago, people had died at the crossing and two extra stop signs had been put there. They were erected as memorials.
  </div>
<div class="tx">
    And so it was: the dead forever change the living. Even those unknown to the dead are required to stop.
  </div>
<div class="tx">
    The sky was still mostly dark, but morning stirred beneath the horizon and birds rustled about in their lofts in the trees and bushes, conversing through murmured chirping.
  </div>
<div class="tx">
    Climbing from the sleeping bag, he put on his socks and boots, unfolded his jacket, and siphoned his arms through the sleeves.
  </div>
<div class="tx">
    Why had he come here, he wondered, and walked down the hill. At the stream, he sat on the bank and stared into the dark water.
  </div>
<div class="tx">
    The air—warm and thick—filled with noises, and mingled with burbling water, rustling birds, and the dry ruckus of squirrels came the distant sounds of humans. Doors slammed, vehicles started, and an occasional, indecipherable, barking voice could be heard. A heavy truck moved along the road beyond the cemetery.
  </div>
<div class="tx">
    Why had he come here?
  </div>
<div class="tx">
    Not everything has a reason, he told himself. His arrival amounted to a whim of circumstance, a living accident. In the same random manner he had arrived in Chicago, Sioux Falls, Cheyenne, San Francisco, Moose Jaw, and many other places. There was no reason.
  </div>
<div class="tx">
    At least this is what he’d been telling himself for years, but he 
     could no longer quite believe it. He now suspected that somewhere between his actions and what he knew about them—in that vast chasm of burgeoning silence—grew a nameless need, pushing him from one place to the next.
  </div>
<div class="tx">
    Something shiny near the water’s edge caught his attention and he investigated.
  </div>
<div class="tx">
    A rusty flashlight, half covered in dead grass and dried mud. Most of the chrome had been chipped or worn off, the cylinder dented in several places.
  </div>
<div class="tx">
    He wondered to whom it belonged. Had it been intentionally discarded or simply lost? But the artifact refused to divulge any information about its owner. Yet <i>someone</i> had obviously occupied the same space that July currently inhabited, and this coincidence begged for explanation.
  </div>
<div class="tx">
    He absently rubbed the dirt from the glass lens with his thumb and pushed the corroded switch forward. To his astonishment, a beam of light leaped out.
  </div>
<div class="tx">
    It seemed impossible, or at least highly improbable, and he experienced an unexpectedly good feeling over having a valuable object in his hands. The dead had come alive. A personal connection grew up between the previous owner and himself: <i>I have something of yours, something worth having.</i>
</div>
<div class="tx">
    But as soon as this cheerful happenstance had been announced, the light dimmed to faint orange. It flickered as though trying to communicate, glowed feebly, and went out.
  </div>
<div class="tx">
    He shook the flashlight and worked the switch forward and back several more times. Nothing.
  </div>
<div class="tx">
    He tossed it on the bank beside him, then picked it up and tried again. Nope.
  </div>
<div class="tx">
    Loneliness soon visited him, and though he had learned to cherish his own private loneliness, this particular feeling had a more universal character. The previous owner of the useless flashlight somehow participated in it. <i>I have something of yours, and it is worthless.</i>
</div>
<div class="tx">
    July looked back at the dark water and understood that he had gone as far as he could. His life had grown too thin, and he was nearing the end of himself. He was living but didn’t feel alive. He knew 
     no one in the sense of understanding them from the inside—feeling the center of their life—and no one knew him.
  </div>
<div class="tx">
    He had come here, he knew then, as a last stand—to either become in some way connected to other people or to die. He could no longer live as a hungry ghost.
  </div>
<div class="tx">
    He retrieved his duffel bag, climbed the woven wire fence, crossed through the cemetery, and began walking into Words. Whatever people he found there would occupy him in one way or another for the rest of his life. For better or worse, this place would become his home.
  </div>
<div>
     <br/>
</div>
<div class="tx1">
    All of these memories visited July as he watched the panther pacing along the fence in the fog. To show the animal that he too knew how to play the game, he stepped out of the barn and walked toward it.
  </div>
<div class="tx">
    The animal stopped pacing, leaped effortlessly over the fence, and disappeared.
  </div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// davi_9781571318008_oeb_c01_r1.vue?62ee80ed